import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { theme } from '../../../styles/theme';
import Page from '../../../common/Page';
import Loader from '../../../common/Loader';
import EmptyState from '../../../common/EmptyState';
import ConfirmModal from '../../../common/ConfirmModal';
import { fieldStyles } from '../../../common/PrivateEventFields';
import { getWithdrawalEvent, sendWithdrawalNotification } from '../../../common/api';
import { formatEventDateTime } from '../../../common/dates';
import { displayEventName } from '../../../common/events';
import { useCurrentUser, isSuperAdmin } from '../../../common/UserContext';

const LIST_PATH = '/admin/withdrawal';

// Invio della notifica di ritiro per un evento: indirizzo modificabile, conteggio biglietti validati, conferma
function WithdrawalEvent() {
  const location = useLocation();
  const eventKey = new URLSearchParams(location.search).get('eventKey') || '';
  const navigate = useNavigate();
  const user = useCurrentUser();
  const allowed = isSuperAdmin(user);

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [address, setAddress] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // silent: ricarica i conteggi senza mostrare lo spinner (così i toast restano visibili)
  const load = useCallback((silent = false) => {
    if (!eventKey) {
      setIsLoading(false);
      return;
    }
    if (!silent) setIsLoading(true);
    getWithdrawalEvent(eventKey)
      .then((result) => {
        setData(result);
        // prefill "nome locale - indirizzo evento": lo stesso testo che l'email usa se non si indica un altro luogo
        setAddress(result.defaultAddress || result.event.where || '');
      })
      .catch((error) => {
        console.error('Error fetching event:', error);
        toast.error(error.status === 404 ? 'Evento non trovato' : "Errore nel caricamento dell'evento");
      })
      .finally(() => setIsLoading(false));
  }, [eventKey]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const result = await sendWithdrawalNotification({ eventKey, address: address.trim() });
      toast.success(`Notifica di ritiro inviata a ${result.notified} clienti`);
      navigate(LIST_PATH);
    } catch (error) {
      console.error('Error sending withdrawal notification:', error);
      if (error.status === 502 && error.body) {
        toast.error(error.body.message || 'Alcuni biglietti non sono stati aggiornati, riprova');
        load(true);
      } else if (error.status === 409) {
        toast.error('Notifica di ritiro già inviata per questo evento');
      } else {
        toast.error(error.message || "Errore nell'invio della notifica");
      }
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  if (!allowed) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (isLoading) {
    return <Loader />;
  }

  const event = data && data.event;
  const totals = (data && data.totals) || { tickets: 0, validated: 0, alreadyNotified: 0 };
  const toNotify = Math.max(totals.validated - totals.alreadyNotified, 0);
  const canSend = toNotify > 0 && address.trim().length > 0 && event && !event.emailDeliverySent;

  return (
    <Page>
      <div style={styles.content}>
        <div style={styles.topRow}>
          <button onClick={() => navigate(LIST_PATH)} style={styles.backArrow} aria-label="Indietro">←</button>
          <span style={styles.typeBadge}>Notifica ritiro</span>
        </div>

        {!event ? (
          <EmptyState text="Evento non trovato" />
        ) : (
          <>
            <h4 style={styles.eventTitle}>{displayEventName(event.eventName)}</h4>
            <div style={styles.dateText}>{formatEventDateTime(event.when)}</div>

            <div style={styles.countersRow}>
              <Counter label="Biglietti" value={totals.tickets} />
              <Counter label="Validati" value={totals.validated} />
              <Counter label="Già notificati" value={totals.alreadyNotified} />
            </div>

            <label style={fieldStyles.label}>
              Indirizzo di ritiro
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Dove i clienti possono ritirare le ceramiche"
                style={fieldStyles.input}
              />
              <span style={styles.hint}>
                Precompilato con nome e indirizzo dell'evento: lascialo così oppure indica un altro luogo di ritiro.
              </span>
            </label>

            {event.emailDeliverySent && (
              <p style={styles.info}>Notifica già inviata{event.emailDeliverySentAt ? ` il ${formatEventDateTime(event.emailDeliverySentAt)}` : ''}.</p>
            )}
            {!event.emailDeliverySent && toNotify === 0 && (
              <p style={styles.info}>Nessun biglietto validato da notificare.</p>
            )}

            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
              style={canSend ? styles.sendButton : styles.sendButtonDisabled}
            >
              Invia Notifica
            </button>
          </>
        )}
      </div>

      {confirmOpen && event && (
        <ConfirmModal
          title="Inviare la notifica di ritiro?"
          message={`Invierai la notifica di ritiro a ${toNotify} clienti per "${displayEventName(event.eventName)}".\nIndirizzo di ritiro: ${address.trim()}`}
          confirmLabel="Invia"
          busy={sending}
          onConfirm={handleSend}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </Page>
  );
}

function Counter({ label, value }) {
  return (
    <div style={styles.counter}>
      <div style={styles.counterValue}>{value}</div>
      <div style={styles.counterLabel}>{label}</div>
    </div>
  );
}

const styles = {
  content: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius,
    boxShadow: theme.shadows.main,
    color: theme.colors.text,
    width: '100%',
    maxWidth: '600px',
    padding: '10px 20px 25px 20px',
    boxSizing: 'border-box',
    marginBottom: '25px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backArrow: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: theme.colors.brand,
    padding: 0,
  },
  typeBadge: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    padding: '2px 10px',
  },
  eventTitle: {
    textAlign: 'center',
    fontSize: '20px',
    margin: '5px 0 0 0',
  },
  dateText: {
    textAlign: 'center',
    fontSize: 15,
    color: theme.colors.textSecondary,
  },
  countersRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  counter: {
    minWidth: '90px',
    padding: '8px 12px',
    borderRadius: theme.borderRadius,
    backgroundColor: theme.colors.background,
    textAlign: 'center',
  },
  counterValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: theme.colors.brand,
  },
  counterLabel: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
  },
  hint: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    fontWeight: 'normal',
  },
  info: {
    margin: 0,
    fontSize: '14px',
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  sendButton: {
    padding: '12px 20px',
    backgroundColor: theme.colors.brand,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  sendButtonDisabled: {
    padding: '12px 20px',
    backgroundColor: theme.colors.border,
    color: theme.colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    fontSize: '16px',
  },
};

export default WithdrawalEvent;
